import NamaCommon from "@/utils/common/NamaCommon.ts";
import NamaURLS from "@/utils/constants/NamaURLS.ts";
import CollectionUtils from "@/utils/common/CollectionUtils.ts";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import EmbeddedViewUtils from "@/utils/editView/editScreens/EmbeddedViewUtils.ts";
import {ref, Ref} from "vue";
import DialogHandler, {CommonDialogAction} from "@/utils/forms/DialogHandler.ts";
import {WidgetsPresenter} from "@/utils/presenters/interfaces.ts";
import Translator from "@/utils/common/Translator.ts";
import ReportLauncherPresenter from "@/utils/presenters/ReportLauncherPresenter.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import ReportViewerPlace from "@/utils/presenters/ReportViewerPlace.ts";
import {useCrossFilterStore} from "@/stores/crossFilterStore.ts";
import ReportMetadata = namaTypes.ReportMetadata;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import DTOUserDashbordChartLine = namaTypes.DTOUserDashbordChartLine;
import DTOBIDashboardResponse = namaTypes.DTOBIDashboardResponse;

export default class DashboardPresenter {
  dashboardId?: string;
  metadata?: ReportMetadata;
  parametersMapping: Record<string, any> = {};
  place?: ReportViewerPlace;
  questionFieldsPresenter: QuestionFieldsPresenter = new QuestionFieldsPresenter();
  searchContextProvider?: any;
  fetchedData: Ref<DTOBIDashboardResponse | undefined> = ref();
  displayDashboard: Ref<boolean> = ref(false);
  // Set by the drill-down popup while it owns the shared cross-filter store; the auto-refresh timers skip
  // their tick while paused (displayDashboard cannot be used for this: Welcome.vue v-ifs the page on it).
  autoRefreshPaused: boolean = false;
  activeSubDashboardId: Ref<string | undefined> = ref();
  private firstFetch: boolean = false;
  private refreshPer: number = 0;
  prefilledQuestions?: DTOFilledQuestionField[];
  // Drill-down context: when set, every FetchBICharts request (full fetch, widget refresh, cross-filter refresh)
  // carries the source widget code + drill filters so the server filters each widget by the clicked value.
  drillDownSourceWidgetCode?: string;
  drillDownFilters?: DTOFilledQuestionField[];

  setDrillDownContext(sourceWidgetCode: string | undefined, drillDownFilters: DTOFilledQuestionField[] | undefined) {
    this.drillDownSourceWidgetCode = sourceWidgetCode;
    this.drillDownFilters = drillDownFilters;
  }

  clearDrillDownContext() {
    this.setDrillDownContext(undefined, undefined);
  }

  async fetchCharts(dashboardId?: string) {
    if (dashboardId !== this.dashboardId || !this.metadata) {
      this.dashboardId = dashboardId;
      await this.fetchMetadata(dashboardId);
    } else {
      this.dashboardId = dashboardId;
      await this.doFetchCharts(dashboardId, false, false);
    }
  }

  async doFetchCharts(dashboardId: string | undefined, fromDashBoardRefresh: boolean, fromWidgetRefresh: boolean) {
    let response = await NamaCommon.namaFetch(NamaURLS.FetchBICharts, {
      data: {
        ...this.buildFetchChartsRequest(dashboardId || '', ''),
        reportId: this.place?.reportId || '',
        tempPrintId: this.place?.tempPrintId || '',
      }
    });
    if (response && response.result && response.result.succeded) {
      this.draw(response, fromDashBoardRefresh, fromWidgetRefresh, dashboardId);
    }
  }

  private canAutoRefresh(): boolean {
    return this.displayDashboard.value && !this.autoRefreshPaused;
  }

  private buildFetchChartsRequest(dashboardId: string | undefined, widgetId: string): Record<string, unknown> {
    const crossFilterStore = useCrossFilterStore();
    const request: Record<string, unknown> = {
      id: dashboardId,
      widgetId,
      subDashboardId: this.activeSubDashboardId.value || '',
      questions: this.getQuestionFields(),
      activeCrossFilters: crossFilterStore.getFilledQuestionFields(),
      widgetSlotOverrides: crossFilterStore.getAllWidgetSlotOverrides()
    };
    if (this.drillDownSourceWidgetCode && this.drillDownFilters?.length) {
      request.drillDownSourceWidgetCode = this.drillDownSourceWidgetCode;
      request.drillDownFilters = this.drillDownFilters;
    }
    return request;
  }

  async switchTab(subDashboardId: string) {
    if (this.activeSubDashboardId.value === subDashboardId) return;
    this.activeSubDashboardId.value = subDashboardId;
    await this.doFetchCharts(this.dashboardId, false, false);
  }

  private draw(response, fromDashBoardRefresh: boolean, fromWidgetRefresh: boolean, dashboardId: string | undefined) {
    if (!response || !response.data || !response.data.chartLines)
      this.displayDashboard.value = false;
    this.doDraw(response.data, fromDashBoardRefresh, fromWidgetRefresh, dashboardId);
  }

  private doDraw(data: DTOBIDashboardResponse, fromDashBoardRefresh: boolean, fromWidgetRefresh: boolean, dashboardId: string | undefined) {
    this.firstFetch = false;
    this.dashboardId = dashboardId;
    const serverActiveSubId = data?.activeSubDashboardId;
    if (serverActiveSubId)
      this.activeSubDashboardId.value = serverActiveSubId;
    if (fromWidgetRefresh && this.fetchedData.value)
      this.mergeRefreshedWidgetLines(data.chartLines ?? []);
    else {
      this.refreshPer = data.refreshPer ?? 0;
      this.fetchedData.value = data;
    }
    this.displayDashboard.value = true;
    const linesToScheduleTimers = fromWidgetRefresh ? (data.chartLines ?? []) : (this.fetchedData.value?.chartLines ?? []);
    for (let line of linesToScheduleTimers) {
      let widgetRefreshPer: number = line.refreshPer ?? 0;
      if (widgetRefreshPer > 0 && !fromDashBoardRefresh) {
        setTimeout(async () => {
          if (this.canAutoRefresh())
          {
            await this.redrawWidgets(line.widgetId);
          }
          return false;
        }, widgetRefreshPer);
      }
    }
    let dashBoardRefreshInMS: number = this.refreshPer;
    if (dashBoardRefreshInMS > 0 && !fromWidgetRefresh) {
      setTimeout(async () => {
        if (this.canAutoRefresh())
        {
          await this.redrawWidgets("");
        }
        return false;
      }, dashBoardRefreshInMS);
    }
  }

  private mergeRefreshedWidgetLines(refreshedLines: DTOUserDashbordChartLine[]) {
    if (!this.fetchedData.value || !refreshedLines.length)
      return;
    const existing = this.fetchedData.value.chartLines ?? [];
    const merged = [...existing];
    for (const refreshedLine of refreshedLines) {
      const idx = merged.findIndex(l => l.widgetId === refreshedLine.widgetId);
      if (idx >= 0)
        merged[idx] = refreshedLine;
      else
        merged.push(refreshedLine);
    }
    this.fetchedData.value = {
      ...this.fetchedData.value,
      chartLines: merged,
    };
  }

  public getQuestionFields(): DTOFilledQuestionField[] {
    let questionFields: DTOFilledQuestionField[] = this.questionFieldsPresenter.getQuestionFields();
    if (this.searchContextProvider && this.parametersMapping) {
      let questionsFromMapping: DTOFilledQuestionField[] = EmbeddedViewUtils.createQuestionFromMapping(this.searchContextProvider, this.parametersMapping);
      EmbeddedViewUtils.overrideQuestions(questionFields, this.parametersMapping, questionsFromMapping);
    }
    return questionFields;
  }

  private async fetchMetadata(dashboardId: string | undefined) {
    let response = await NamaCommon.namaFetch(NamaURLS.FetchChartsMetadata, {
      params: {dashBoardId: dashboardId}
    });
    if (response && response.result && response.result.succeded) {
      let fetchedCharts: ReportMetadata[] = response.list ?? [];
      await this.doSetMetadata(fetchedCharts, dashboardId);
    }
  }

  async redrawWidgets(widgetId: string | undefined) {
    let response = await NamaCommon.namaFetch(NamaURLS.FetchBICharts, {
      data: this.buildFetchChartsRequest(this.dashboardId, widgetId ?? '')
    });
    if (response && response.result && response.result.succeded) {
      this.draw(response, ObjectChecker.isEmptyOrNull(widgetId), ObjectChecker.isNotEmptyOrNull(widgetId), this.dashboardId);
    } else {
      console.log("fail on welcome view imp");
    }
  }

  private async doSetMetadata(fetchedMetadata: ReportMetadata[], dashboardId: string | undefined) {
    this.metadata = {};
    if (fetchedMetadata && fetchedMetadata.length) {
      for (let subMetadata of fetchedMetadata) {
        DashboardPresenter.merge(this.metadata, subMetadata);
      }
    }
    if (this.prefilledQuestions && this.prefilledQuestions.length > 0) {
      const questionFields = this.metadata?.questionFields;
      await this.questionFieldsPresenter.edit(questionFields, this.prefilledQuestions);
      await this.doFetchCharts(dashboardId, false, false);
    }
    else if (!this.metadata.questionFields || this.allQuestionsAreHidden()) {
      const questionsFromMapping = EmbeddedViewUtils.createQuestionFromMapping(this.searchContextProvider, this.parametersMapping);
      const questionFields = this.metadata?.questionFields;
      await this.questionFieldsPresenter.edit(questionFields, questionsFromMapping);
      await this.doFetchCharts(dashboardId, false, false);
    }
    else
      await this.showQuestionsDialog();
  }

  private async showQuestionsDialog() {
    let dashboardId: string | undefined = this.dashboardId;
    let okBtn: CommonDialogAction = CommonDialogAction.createOkDialogAction(async (object: Record<string, any>) => {
      if (this.questionFieldsPresenter.isAnyRequiredFieldOrGroupEmpty()) {
        return;
      }
      if (this.firstFetch)
        await this.doFetchCharts(dashboardId, false, false);
      else
        await this.redrawWidgets("");
    });
    let cancelBtn: CommonDialogAction = CommonDialogAction.createCancelDialogAction(() => {
    });

    let questionsFromMapping: DTOFilledQuestionField[] = EmbeddedViewUtils.createQuestionFromMapping(this.searchContextProvider, this.parametersMapping);
    let defaultValues: DTOFilledQuestionField[] = ReportLauncherPresenter.createDefaultValues(this.metadata);
    if (!this.firstFetch)
      defaultValues = this.questionFieldsPresenter.getQuestionFields();
    CollectionUtils.addIfNotFound(questionsFromMapping, defaultValues);
    let hiddenQuestions: string[] = [];
    if (this.parametersMapping)
      hiddenQuestions = this.parametersMapping.hiddenParameters ?? [];
    await this.questionFieldsPresenter.edit(this.metadata?.questionFields ?? [], questionsFromMapping ?? [], hiddenQuestions);
    let dialogBox = new DialogHandler([okBtn, cancelBtn], Translator.translateId("parameters"), false);
    await dialogBox.createPresenter(this.questionFieldsPresenter as WidgetsPresenter, this.metadata?.questionFields, questionsFromMapping);
    await dialogBox.runDialogBox();
  }

  private allQuestionsAreHidden() {
    return this.parametersMapping && this.metadata?.questionFields?.every(questionField => this.parametersMapping.hiddenParameters?.includes(questionField.id ?? ''));
  }

  private static merge(metadata: ReportMetadata, subMetadata: ReportMetadata) {
    if (!metadata.metadata)
      metadata.metadata = [];
    CollectionUtils.addListIfNotFound(metadata.metadata, subMetadata.metadata);
    if (!metadata.questionFields)
      metadata.questionFields = [];
    CollectionUtils.addListIfNotFound(metadata.questionFields, subMetadata.questionFields);
    subMetadata.allParameterIds = subMetadata.allParameterIds ?? []
    for (let param of subMetadata.allParameterIds) {
      if (!metadata.allParameterIds) {
        metadata.allParameterIds = [];
        metadata.allParameterIds.push(param)
      } else if (!metadata.allParameterIds.contains(param)) {
        metadata.allParameterIds.push(param);
      }
    }
  }
}
